// Caller-device rendering for call histories: parse the User-Agent captured
// at initiate into an OS family + human label, and render it as a small icon
// whose native tooltip (title) shows the raw User-Agent string.

export type DeviceOs = 'android' | 'ios' | 'windows' | 'macos' | 'linux' | 'unknown';

export interface DeviceInfo {
  os: DeviceOs;
  /** Short human label, e.g. "Android · Pixel 8", "iPhone", "Windows". */
  label: string;
  /** Device model when derivable (client hints, or Android UA token). */
  model?: string;
}

/** Extract the Android device model from a classic (non-reduced) UA.
 *  Modern Chrome reduces it to the frozen token "K" — treat that as absent. */
function androidModelFromUa(ua: string): string | undefined {
  const match = ua.match(/Android [\d.]+;\s*([^;)]+?)(?:\s+Build\/|\))/);
  const model = match?.[1]?.trim();
  if (!model || model === 'K') return undefined;
  return model;
}

export function parseDeviceInfo(
  userAgent?: string | null,
  deviceModel?: string | null
): DeviceInfo | null {
  const ua = userAgent?.trim();
  const model = deviceModel?.trim() || undefined;
  if (!ua && !model) return null;

  const os: DeviceOs = !ua
    ? 'unknown'
    : /android/i.test(ua)
      ? 'android'
      : /iphone|ipad|ipod/i.test(ua)
        ? 'ios'
        : /windows/i.test(ua)
          ? 'windows'
          : /macintosh|mac os x/i.test(ua)
            ? 'macos'
            : /linux|cros/i.test(ua)
              ? 'linux'
              : 'unknown';

  const osLabel: Record<DeviceOs, string> = {
    android: 'Android',
    ios: ua && /ipad/i.test(ua) ? 'iPad' : 'iPhone',
    windows: 'Windows',
    macos: 'Mac',
    linux: 'Linux',
    unknown: 'Dispositivo',
  };

  const resolvedModel = model || (os === 'android' && ua ? androidModelFromUa(ua) : undefined);
  const label = resolvedModel ? `${osLabel[os]} · ${resolvedModel}` : osLabel[os];
  return { os, label, model: resolvedModel };
}

const isMobile: Record<DeviceOs, boolean> = {
  android: true,
  ios: true,
  windows: false,
  macos: false,
  linux: false,
  unknown: false,
};

const phonePath =
  'M7 3.5h10a1 1 0 011 1v15a1 1 0 01-1 1H7a1 1 0 01-1-1v-15a1 1 0 011-1zM11 17.5h2';
const desktopPath =
  'M4 5.5h16a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1v-9a1 1 0 011-1zM9 20.5h6M12 16.5v4';
const unknownPath =
  'M12 21a9 9 0 100-18 9 9 0 000 18zM9.75 9.75a2.25 2.25 0 114.5 0c0 1.5-2.25 1.875-2.25 3.375M12 16.5h.01';

/**
 * Device icon + short label for call-history tables. The native `title`
 * tooltip exposes the raw User-Agent (plus the client-hints model when present).
 */
export function DeviceIcon({
  userAgent,
  deviceModel,
  className = 'h-4 w-4 shrink-0',
}: {
  userAgent?: string | null;
  deviceModel?: string | null;
  className?: string;
}) {
  const info = parseDeviceInfo(userAgent, deviceModel);
  if (!info) return <span className="text-neutral-300">—</span>;

  const path =
    info.os === 'unknown' ? unknownPath : isMobile[info.os] ? phonePath : desktopPath;
  const tooltip = userAgent ? `${info.label}\nUA: ${userAgent}` : info.label;

  return (
    <span
      title={tooltip}
      className="inline-flex max-w-[180px] cursor-help items-center gap-1.5 text-neutral-500"
    >
      <svg
        className={className}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={path} />
      </svg>
      <span className="truncate text-xs">{info.label}</span>
    </span>
  );
}
